import type { RegionRendererDefinition } from '@/domain/world/region-definition';
import type Phaser from 'phaser';
import type { RegionDefinition } from '@/domain/world/region-definition';
import { logger } from '@/infra/logger/Logger';
import { buildLayeredRegionRenderer } from './LayeredRegionRenderer';
import { buildProceduralRegionRenderer } from './ProceduralRegionRenderer';
import type {
  LayeredRegionManifest,
  ResolvedRegionRendererKind,
  RegionRendererHandle,
} from './types';
import { validateRegionAssetBundle } from '@/domain/world/region-assets';

export const validateLayeredRegionManifest = validateRegionAssetBundle;

export function resolveRegionRendererKind(
  definition: RegionRendererDefinition,
  assetsAvailable: boolean,
): ResolvedRegionRendererKind {
  return definition.kind === 'layered-raster' && assetsAvailable
    ? 'layered-raster'
    : 'procedural';
}

function manifestTexturesExist(scene: Phaser.Scene, manifest: LayeredRegionManifest): boolean {
  const keys = [
    manifest.base,
    ...manifest.belowActors.map((layer) => layer.src),
    ...manifest.ySortedProps.map((layer) => layer.src),
    ...manifest.aboveActors.map((layer) => layer.src),
  ];
  return keys.every((key) => scene.textures.exists(key));
}

export function buildRegionRenderer(
  scene: Phaser.Scene,
  region: RegionDefinition,
): RegionRendererHandle {
  if (region.renderer.kind === 'procedural') {
    return buildProceduralRegionRenderer(scene, region);
  }

  const manifest = validateLayeredRegionManifest(
    scene.cache.json.get(region.renderer.manifestUrl) as unknown,
  );
  if (manifest && manifestTexturesExist(scene, manifest)) {
    return buildLayeredRegionRenderer(scene, region, manifest);
  }

  logger.warn('world.region-renderer-fallback', {
    regionId: region.id,
    manifestUrl: region.renderer.manifestUrl,
  });
  return buildProceduralRegionRenderer(scene, region);
}
